import { instantOfWall, wallOf } from "../shared/time-zone-offset";
import { Flight } from "./models/flight.model";

/** Each moment of a flight: its day and time as the organization keeps them, and the same in UTC for a flight that has them. */
const MOMENTS = [
    { date: "startDate", time: "startTime", dateUtc: "startDateUtc", timeUtc: "startTimeUtc" },
    { date: "flightDate", time: "takeOffTime", dateUtc: "flightDateUtc", timeUtc: "takeOffTimeUtc" },
    { date: "arrivalDate", time: "landingTime", dateUtc: "arrivalDateUtc", timeUtc: "landingTimeUtc" },
    { date: "stopDate", time: "stopTime", dateUtc: "stopDateUtc", timeUtc: "stopTimeUtc" },
] as const;

const originals = new WeakMap<Flight, Flight>();

/** The flight as the organization keeps it, whatever time zone the row shows: the edit works on this one. */
export const originalOf = (flight: Flight): Flight => originals.get(flight) ?? flight;

/** The instant of a moment: from its UTC values when the flight has them, else from the organization's clocks. */
function instantOf(flight: Flight, moment: (typeof MOMENTS)[number], organizationZone: string): number | null {
    const dayUtc = flight[moment.dateUtc];
    const timeUtc = flight[moment.timeUtc];
    if (dayUtc && timeUtc) return Date.parse(`${dayUtc.slice(0, 10)}T${timeUtc.length === 5 ? `${timeUtc}:00` : timeUtc}Z`);
    const day = flight[moment.date];
    const time = flight[moment.time];
    return day && time ? instantOfWall(day, time, organizationZone) : null;
}

/**
 * The flight with its days and times read by the clocks of `zone`. The flight arrives in the organization's time
 * zone, so for that zone it is returned as it is. A moment missing its day or its time stays as it is.
 */
export function inTimeZone(flight: Flight, organizationZone: string, zone: string): Flight {
    if (zone === organizationZone) return flight;
    const shown: Flight = { ...flight };
    for (const moment of MOMENTS) {
        const instant = instantOf(flight, moment, organizationZone);
        if (instant === null) continue;
        const wall = wallOf(instant, zone);
        shown[moment.date] = wall.day;
        shown[moment.time] = wall.time;
    }
    originals.set(shown, flight);
    return shown;
}
